import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';

import { navigationService } from 'src/config/rootService';
import { Dispatch, RootState } from 'src/config/rootStore';
import logoImg from 'src/public/img/mh-logo-text-modern.svg';
import {
    Button,
    ButtonGroup,
    Form,
    FormInputPassword,
    FormInputText,
    HeadingText50,
    useForm,
} from '@modernhealth/vital-components';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font } from 'src/shared/styles/variables';
import { LoginFormTestId } from './testIds';

import {
    getAuthenticationErrorMessage,
    isLoginInProgress,
} from '../selectors/authenticationSelectors';
import { InternalLink } from 'src/shared/components/InternalLink';

interface RegistrationErrorProps {
    displayError: boolean;
}
type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const ErrorContainer = styled.div<RegistrationErrorProps>(({ displayError }) => ({
    display: displayError ? 'inline' : 'none',
    borderRadius: '4px',
    backgroundColor: `${hexToRgba(color.cornflowerBlueDark, 0.1)}`,
    width: '100%',
}));
const ErrorMessage = styled.div(
    {
        color: `${color.blueBayoux}`,
        lineHeight: '24px',
        padding: '12px',
    },
    font.roman(font.size.medium),
);
const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '10em',
    cursor: 'pointer',
    marginRight: 12,
});
const FormContainer = styled.div({
    maxWidth: '500px',
    width: '100%',
});
const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: '112px',
    paddingBottom: '14px',
    justifyContent: 'center',
    flexGrow: 1,
});

interface LocationProps {
    existingAccount: boolean;
}

const LoginForm: React.FC<Props> = ({ loginInProgress, login, errorMessage }) => {
    const { t } = useTranslation();
    const location = useLocation<LocationProps>();
    const existingAccount = location.state?.existingAccount;
    const displayError = errorMessage || existingAccount ? true : false;

    const loginFormDefinition = useForm();

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = 'https://www.modernhealth.com';
                }}
            />
            <Container>
                <FormContainer>
                    <HeadingText50>{t('auth:login_form.title')}</HeadingText50>
                    <Form onSubmit={login} definition={loginFormDefinition}>
                        <ErrorContainer
                            displayError={displayError}
                            data-testid={LoginFormTestId.ErrorMessage}
                        >
                            {errorMessage ? (
                                <ErrorMessage>{t(errorMessage)}</ErrorMessage>
                            ) : (
                                <ErrorMessage>
                                    {t('auth:login_form.already_have_account')}
                                </ErrorMessage>
                            )}
                        </ErrorContainer>
                        <FormInputText
                            name="email"
                            label={t('auth:common_form_inputs.labels.email')}
                            trim
                        />
                        <FormInputPassword
                            name="password"
                            label={t('auth:common_form_inputs.labels.password')}
                            trim
                        />
                        <InternalLink to="/password-reset">
                            {t('auth:authorization_box.forgot_your_password')}
                        </InternalLink>
                        <ButtonGroup>
                            <Button
                                onClick={() => navigationService.navigate('/')}
                                variant="secondary"
                                size="large"
                            >
                                {t('auth:login_form.back')}
                            </Button>
                            <Button type="submit" size="large" busy={loginInProgress}>
                                {t('auth:login_form.continue')}
                            </Button>
                        </ButtonGroup>
                        <InternalLink
                            data-testid={LoginFormTestId.SSOButton}
                            to="/single-sign-on"
                            style={{
                                width: '100%',
                                textAlign: 'center',
                            }}
                        >
                            {t('auth:login_form.log_in_using_single_sign_on')}
                        </InternalLink>
                    </Form>
                </FormContainer>
            </Container>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    errorMessage: getAuthenticationErrorMessage(state),
    loginInProgress: isLoginInProgress(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    login: dispatch.authentication.login,
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
